/**
 * model/lockstopPlacement.ts — Posizionamento del lockstop (piastra di
 * rinforzo) sul connettore (28-09-2026, richiesta utente).
 *
 * REGOLE (confermate con l'utente passo per passo, con screenshot reali):
 * - `installationLevels` = quanti PEZZI FISICI separati (non righe): 1 = un
 *   solo pezzo, SOLO joist. 2 = due pezzi SEPARATI, uno intero sul joist e
 *   uno intero sull'header — MAI un pezzo solo che fa da ponte fra i due
 *   lati (corretto 28-09-2026, prima ipotesi sbagliata: "riga alta→joist,
 *   riga bassa→header" nello STESSO pezzo).
 * - Ogni pezzo usa TUTTI i propri fori (tutte le righe/colonne della
 *   propria quota), allineati a un blocco di righe CONSECUTIVE del
 *   connettore, sullo STESSO lato — confermato sul caso base LOCKT3580 +
 *   LOCKSTOP35 (4 fori usati tutti, sul solo joist, per installationLevels=1).
 * - Il blocco di righe del connettore e' SEMPRE quello piu' vicino al
 *   CONFINE CONDIVISO fra joist e header nel mondo — i due pezzi convergono
 *   l'uno verso l'altro, non puntano entrambi "in alto" (corretto
 *   29-09-2026, richiesta utente esplicita sul lato header). Per il JOIST
 *   quel confine e' il world-TOP: `connectorPlacement.ts` compone un flip
 *   Rx180° a livello MONDO sopra alla sola conversione Ry(-90°) (che non
 *   tocca Y), quindi Y mondo = -Y locale, e la riga world-TOP e' quella con Y
 *   LOCALE PIU' PICCOLA (bug scoperto dall'utente: "il sistema di
 *   riferimento locale ha l'asse y positivo verso il basso, che corrisponde
 *   a y negativo globale" — verificato: LOCKT35120+LOCKSTOP35 "confermato
 *   corretto" in una sessione precedente usava in realta' il blocco di righe
 *   PIU' IN BASSO nel mondo, mascherato dal fatto che lo zigzag e' periodico
 *   A,B,A,B e quindi qualunque blocco di 2 righe consecutive da' fori che
 *   combaciano ugualmente). Per l'HEADER (solo Ry(+90°), nessun flip, Y
 *   mondo = Y locale) quel confine e' invece il world-BOTTOM — che e'
 *   ANCH'ESSO la Y locale piu' piccola: `selectTopRowBlock` usa quindi la
 *   STESSA formula (Y locale minima) per entrambi i lati, per ragioni
 *   opposte.
 * - SKU "larghi" (kind="wide", es. LOCKSTOP35/50/75/100/125): un'unica
 *   istanza allineata su TUTTE le colonne del blocco, SENZA MAI ruotare il
 *   pezzo per far combaciare i fori — l'orientamento e' fisso, quello del
 *   connettore (28-09-2026, richiesta utente esplicita: un tentativo di
 *   specchiare il pezzo per correggere un mismatch e' stato respinto —
 *   "sei uscito dai vincoli"; il mismatch era in realta' il bug del blocco
 *   sbagliato sopra, non un problema di orientamento). Se i fori non
 *   combaciano con il blocco piu' in alto, il problema e' nei DATI del
 *   connettore o della piastra: va segnalato, non aggirato con una
 *   rotazione.
 * - SKU "stretti" (kind="narrow", es. LOCKSTOP5/5U/7): un'istanza per
 *   ciascuna colonna ESTREMA del blocco (solo 2, anche se ce ne sono di
 *   piu' in mezzo) — o tutte se il blocco ne ha solo 1 o 2 colonne, SEMPRE
 *   con lo stesso orientamento diretto (NESSUNO specchio fra i due pezzi).
 *   Uno specchio 180° sul secondo pezzo era stato aggiunto il 28-09-2026 per
 *   un caso (LOCKT53120+LOCKSTOP5) dove appariva ruotato male — rimosso il
 *   29-09-2026: era un tentativo di compensare il bug del blocco-riga
 *   sbagliato sopra (verificato: con il blocco corretto ENTRAMBI i pezzi
 *   combaciano usando lo stesso orientamento diretto, sia su quel caso sia
 *   su LOCKT35100+LOCKSTOP5).
 *   AGGIORNAMENTO 16-10-2026: il pezzo sulla colonna DESTRA e' ruotato di 180°
 *   attorno a Z (specchio visivo, fori coincidenti) — vedi `targetColumnGroups`.
 * - Con 2 connettori (`nConnectors=2`, 16-10-2026, richiesta utente): per
 *   ciascun lato (joist / header) i due connettori affiancati sono UN SOLO
 *   BLOCCO — come per la distinta, dove la quantita' e' quella della coppia
 *   (`lock_pair_lockstop_mapping`) e non la somma dei due connettori. Le righe
 *   di fori dei due connettori si uniscono nel frame del primo (`selectBlockRows`):
 *   i pezzi "narrow" vanno solo alle DUE estremita' del blocco (non in mezzo,
 *   fra i due connettori), quelli "wide" sono uno solo sull'intero blocco.
 * - Ogni pezzo e' un corpo rigido: un solo punto di riferimento (il primo
 *   foro del blocco) basta ad ancorarlo, perche' passo colonne E passo
 *   righe combaciano gia' fra quota lockstop e fori reali del connettore
 *   (verificato piu' volte con dati reali, incluso parsando i vertici GLB).
 *   L'offset del foro di riferimento dal CENTRO GEOMETRICO della piastra
 *   (dove la mesh GLB ha la propria origine, dopo la conversione
 *   autoring→viewer) va sottratto per ancorare il centro, non il foro
 *   stesso (bug corretto 28-09-2026, screenshot LOCKT35120+LOCKSTOP35).
 * - Profondita' (asse Z locale, "a filo con la faccia esterna del
 *   connettore", 29-09-2026): NON si usa `hole.position.z` (una quota GLB
 *   per-asset, verificata inaffidabile — su LOCKT3580 e' 7.25mm contro un
 *   valore di calcolo di 7.0mm, scarto piccolo ma non garantito su tutti gli
 *   asset). Si usa invece `screwEdgeThicknessMm`
 *   (`lock_calculator.lock.screw_edge_thickness`, gia' usato per le formule
 *   di calcolo ritenuta/taglio in eta_190831_lock.go — la stessa fonte
 *   autorevole, non una quota derivata dalla mesh) — Z=0 e' la faccia a
 *   contatto con la trave (confermato dall'utente), quindi la faccia esterna
 *   e' esattamente a Z=screwEdgeThicknessMm. Fallback su `hole.position.z`
 *   solo se il valore di catalogo non e' ancora risolto (null).
 *
 * Layer `model/` (SPEC §6): three.js solo per l'algebra (THREE.Group.
 * localToWorld, come gia' fa `model/fasteners.ts`), nessun side-effect.*/

import * as THREE from "three";
import type { ConnectorSide, HoleFeature, LockstopFeatures, Vec3 } from "../data/types";
import type { ConnectorInstance } from "./types";

export type LockstopKind = "wide" | "narrow";

/**
 * Un foro standard coperto da un lockstop: l'id e' relativo al connettore
 * `connectorIndex` (con 2 connettori un pezzo "wide" copre fori di entrambi).*/
export interface CoveredHole {
  connectorIndex: number;
  holeId: string;
}

/** Una piastra lockstop fisica, gia' risolta (posizione + orientamento mondo). */
export interface LockstopInstance {
  id: string;
  /** Lato del connettore a cui e' agganciato l'intero pezzo. */
  side: ConnectorSide;
  /**
   * 0-based: connettore PROPRIETARIO del pezzo (nConnectors=2 → 0 o 1), quello
   * il cui frame ne ancora la posizione. Con 2 connettori e' il primo del
   * lato: il pezzo puo' coprire anche fori del secondo (`coveredHoles`).*/
  connectorIndex: number;
  position: Vec3;
  quaternion: THREE.Quaternion;
  widthMm: number;
  heightMm: number;
  thicknessMm: number;
  glbUrl: string | null;
  /**
   * Z LOCALE (pre-world) usata per `position` — `screwEdgeThicknessMm` o il
   * fallback (29-09-2026). Serve al render layer per correggere `position`
   * con lo spessore MISURATO sulla mesh reale (vedi
   * `components/locks/MeasureConnectorFlushThickness.tsx`): essendo il
   * frame una pura rotazione+traslazione, un delta sulla Z locale sposta
   * `position` esattamente di `delta * asseZ locale portato nel mondo` —
   * evita di dover rifare qui il calcolo X/Y dell'ancora.*/
  localAnchorZMm: number;
  /**
   * Id dei fori STANDARD del connettore proprietario (stesso `side`/
   * `connectorIndex`) che QUESTO pezzo copre fisicamente (29-09-2026,
   * richiesta utente: "ora bisogna allineare le viti nel caso ci sia un
   * lockstop" — la vite deve attraversare anche lo spessore del lockstop,
   * quindi solo le viti nei fori REALMENTE coperti vanno spostate, non tutte
   * quelle del connettore). Trovato scansionando ogni foro della QUOTA del
   * lockstop, calcolandone la posizione locale attesa sul connettore (stessa
   * formula dell'ancora) e cercando il foro standard reale piu' vicino.*/
  coveredHoles: CoveredHole[];
  /**
   * Coordinate (x,y) CENTRATE (frame post-nodo, `toConnectorFrame`) dei fori
   * della QUOTA del lockstop (29-09-2026) — serve al render layer per
   * misurare lo spessore REALE della piastra vicino a un foro (non il bbox
   * intero: verificato su LOCKSTOP100 che vicino ai fori e' 2.50mm mentre il
   * bbox arriva a 15.5mm — bordi/alette rialzate lontano dai fori, richiesta
   * utente: "mi interessa lo spessore vicino ai fori e basta"), invece dello
   * `thicknessMm` dichiarato (inaffidabile per lo stesso motivo del
   * connettore). Un solo punto (il primo foro) basta: la misura locale e'
   * risultata uniforme su tutti i fori dello stesso pezzo.*/
  holesXY: readonly [number, number][];
}

/** Una riga di fori standard del connettore, in frame LOCALE (pre-world). */
interface ConnectorRow {
  y: number;
  z: number;
  xs: number[]; // ordinate, ascendenti
}

/**
 * Raggruppa i fori standard per riga applicando PRIMA `meshQuaternion` — la
 * stessa correzione che `model/fasteners.ts:holeToFastenerAnchor` applica
 * gia' alle viti per i 14 asset con `component_rotation_deg=180°`: senza,
 * "Y locale piu' alta" per QUEI connettori non e' la riga visivamente in
 * alto (bug trovato 28-09-2026 sui test utente LOCKT50135/50175/100215/
 * 100240 — segnalati come "non vicini alla parte superiore").*/
function groupStandardHolesByRow(holes: HoleFeature[], meshQuaternion: THREE.Quaternion): ConnectorRow[] {
  const byY = new Map<string, { y: number; z: number; xs: number[] }>();
  for (const hole of holes) {
    if (hole.type !== "standard") continue;
    const pos = new THREE.Vector3(hole.position[0], hole.position[1], hole.position[2]).applyQuaternion(meshQuaternion);
    const key = pos.y.toFixed(1);
    const row = byY.get(key) ?? { y: pos.y, z: pos.z, xs: [] };
    row.xs.push(pos.x);
    byY.set(key, row);
  }
  return [...byY.values()].map((row) => ({ ...row, xs: row.xs.sort((a, b) => a - b) })).sort((a, b) => b.y - a.y);
}

/**
 * Blocco delle `rowCount` righe piu' vicine al confine CONDIVISO fra joist e
 * header nel mondo (29-09-2026, richiesta utente: "il lockstop deve
 * trovarsi nella parte bassa [nel mondo]" anche per l'header, non in alto
 * come il joist — i due pezzi convergono verso il confine comune, non
 * puntano entrambi "in alto").
 *
 * Per il JOIST, Y mondo = -Y locale (flip Rx180°, vedi doc-comment in cima):
 * il confine (world-TOP) e' quindi la Y LOCALE PIU' PICCOLA. Per l'HEADER,
 * Y mondo = Y locale (nessun flip): il confine (world-BOTTOM) e' ANCH'ESSO
 * la Y locale PIU' PICCOLA — stessa formula per entrambi i lati, per ragioni
 * opposte. Le righe sono ordinate DESCENDENTI per Y locale
 * (`groupStandardHolesByRow`): le ultime `rowCount` sono quindi il blocco
 * cercato, in entrambi i casi — prese in ordine DESCENDENTE fra loro, cosi'
 * `block[0]` resta "la riga del blocco piu' vicina alla riga-guida della
 * piastra", la convenzione di accoppiamento gia' validata (vedi
 * `computeLockstopInstances`).*/
function selectTopRowBlock(instance: ConnectorInstance, rowCount: number): ConnectorRow[] {
  const rows = groupStandardHolesByRow(instance.features.holes, instance.meshQuaternion);
  return rows.slice(-rowCount);
}

/**
 * Righe del blocco di `rowCount` righe vicine al confine, per l'intero BLOCCO di
 * connettori affiancati di un lato, espresse nel frame di `owner` (il primo).
 * Con un solo connettore coincide con `selectTopRowBlock`. Con 2, le colonne
 * del secondo si portano nel frame del primo passando dal mondo (i frame
 * differiscono solo per la traslazione lungo il blocco) e le righe alla stessa
 * quota si uniscono: `xs` e' l'unione ordinata di tutte le colonne.*/
function selectBlockRows(owner: ConnectorInstance, members: ConnectorInstance[], rowCount: number): ConnectorRow[] {
  const merged = new Map<string, ConnectorRow>();
  for (const member of members) {
    for (const row of selectTopRowBlock(member, rowCount)) {
      const points = row.xs.map((x) => {
        if (member === owner) return new THREE.Vector3(x, row.y, row.z);
        const p = new THREE.Vector3(x, row.y, row.z);
        member.frame.localToWorld(p);
        return owner.frame.worldToLocal(p);
      });
      const key = (points[0]?.y ?? row.y).toFixed(1);
      const entry = merged.get(key) ?? { y: points[0]?.y ?? row.y, z: points[0]?.z ?? row.z, xs: [] };
      for (const p of points) {
        if (!entry.xs.some((x) => Math.abs(x - p.x) < 0.05)) entry.xs.push(p.x);
      }
      merged.set(key, entry);
    }
  }
  return [...merged.values()].map((row) => ({ ...row, xs: row.xs.sort((a, b) => a - b) })).sort((a, b) => b.y - a.y);
}

/** Rotazione 180° attorno a Z locale (profondita'), per il pezzo "narrow" di destra. */
const ROTATE_Z_180 = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 0, 1), Math.PI);

/** Tolleranza (mm) per considerare un foro reale "lo stesso" di un target calcolato. */
const HOLE_MATCH_TOLERANCE_MM = 2;

/**
 * Trova l'id del foro STANDARD del connettore piu' vicino a (localX,localY)
 * (frame locale, dopo `meshQuaternion` — stesso frame di `groupStandardHolesByRow`),
 * entro `HOLE_MATCH_TOLERANCE_MM`. Usato per calcolare `LockstopInstance.coveredHoles`
 * — vedi doc-comment li' per il perche'.*/
function findNearestStandardHoleId(instance: ConnectorInstance, localX: number, localY: number): string | null {
  let bestId: string | null = null;
  let bestDist = Infinity;
  for (const hole of instance.features.holes) {
    if (hole.type !== "standard") continue;
    const pos = new THREE.Vector3(hole.position[0], hole.position[1], hole.position[2]).applyQuaternion(
      instance.meshQuaternion,
    );
    const d = Math.hypot(pos.x - localX, pos.y - localY);
    if (d < bestDist) {
      bestDist = d;
      bestId = String(hole.id);
    }
  }
  return bestDist <= HOLE_MATCH_TOLERANCE_MM ? bestId : null;
}

/**
 * Colonne bersaglio sulla riga guida del blocco: un unico gruppo con TUTTE
 * (kind="wide", SEMPRE orientamento diretto — mai ruotato per combaciare i
 * fori, vedi doc-comment in cima) — oppure un gruppo PER COLONNA selezionata
 * (kind="narrow": le 2 estreme, o tutte se ce ne sono solo 1 o 2). Per i
 * "narrow" il pezzo sulla colonna DESTRA e' ruotato di 180° attorno a Z
 * (16-10-2026, richiesta utente: il lockstop che occupa solo i lati e' una
 * coppia speculare). NON e' un flip di scala: i fori della piastra hanno
 * simmetria 180° (H1<->H2) ma NON speculare, e i fori del connettore sono a
 * zigzag (+5mm a destra scendendo di riga), quindi solo la rotazione li fa
 * combaciare — un flip in X li manderebbe fuori dai fori (verificato su
 * LOCKT53120/LOCKT35100 + LOCKSTOP5). Non e' la rotazione rimossa il
 * 29-09-2026, che compensava il bug del blocco-riga sbagliato.*/
function targetColumnGroups(topRow: ConnectorRow, kind: LockstopKind): { xs: number[]; mirrored: boolean }[] {
  if (kind === "wide") return [{ xs: topRow.xs, mirrored: false }];
  const selected = topRow.xs.length <= 2 ? topRow.xs : [topRow.xs[0]!, topRow.xs[topRow.xs.length - 1]!];
  return selected.map((x, i) => ({ xs: [x], mirrored: i > 0 }));
}

/** Righe DISTINTE (y_mm, ascendenti — la prima e' la piu' in alto), origine in alto a sinistra. */
function lockstopRowsYMm(features: LockstopFeatures): number[] {
  return [...new Set(features.holes.map((h) => h.y_mm))].sort((a, b) => a - b);
}

/** Colonne (x_mm, ascendenti) della riga della quota lockstop con lo y_mm dato. */
function lockstopColumnsForRow(features: LockstopFeatures, yMm: number): number[] {
  return features.holes
    .filter((h) => h.y_mm === yMm)
    .map((h) => h.x_mm)
    .sort((a, b) => a - b);
}

/**
 * Converte un foro della quota lockstop (x_mm/y_mm, origine in alto a
 * sinistra, [0,width_mm]×[0,height_mm]) nel frame CENTRATO che condivide con
 * il connettore (X=larghezza, Y=altezza, entrambi centrati su 0 — verificato
 * parsando i vertici reali del GLB LOCKSTOP35: bbox post-nodo
 * X=[-width/2,+width/2], Y=[-height/2,+height/2], match a 3.1mm uniformi su
 * tutti i fori).*/
function toConnectorFrame(xMm: number, yMm: number, features: LockstopFeatures): { x: number; y: number } {
  return { x: xMm - features.width_mm / 2, y: features.height_mm / 2 - yMm };
}

/**
 * Fori standard dei connettori del blocco coperti dal pezzo ancorato in `anchor`
 * (frame di `owner`): ogni foro della QUOTA del lockstop (non solo il foro di
 * riferimento) atterra su un foro reale di un connettore del blocco — con 2
 * connettori puo' essere del secondo, quindi si cerca in ciascun `member` (il
 * punto si porta nel frame del membro passando dal mondo). `sign` = -1 per il
 * pezzo ruotato di 180°. Vedi `LockstopInstance.coveredHoles`.*/
function findCoveredHoles(
  owner: ConnectorInstance,
  members: ConnectorInstance[],
  features: LockstopFeatures,
  anchor: { x: number; y: number; z: number },
  sign: 1 | -1,
): CoveredHole[] {
  const covered: CoveredHole[] = [];
  for (const plateHole of features.holes) {
    const centered = toConnectorFrame(plateHole.x_mm, plateHole.y_mm, features);
    for (const member of members) {
      const target = new THREE.Vector3(anchor.x + sign * centered.x, anchor.y + sign * centered.y, anchor.z);
      if (member !== owner) {
        owner.frame.localToWorld(target);
        member.frame.worldToLocal(target);
      }
      const holeId = findNearestStandardHoleId(member, target.x, target.y);
      if (holeId !== null) {
        covered.push({ connectorIndex: member.index, holeId });
        break;
      }
    }
  }
  return covered;
}

export interface LockstopPlacementInput {
  features: LockstopFeatures;
  installationLevels: 1 | 2;
  kind: LockstopKind;
  /** `scene.connectors` — coppie joist+header per ogni indice connettore. */
  connectors: ConnectorInstance[];
  glbUrl: string | null;
  /**
   * `lock_calculator.lock.screw_edge_thickness` (mm) — spessore del SOLO
   * metallo di un singolo lato del connettore, dalla stessa fonte usata per
   * le formule di calcolo (non dalla mesh GLB, vedi doc-comment in cima).
   * Z=0 e' la faccia a contatto con la trave: la faccia esterna, dove deve
   * stare il lockstop, e' quindi a Z=screwEdgeThicknessMm. Null → fallback
   * su `hole.position.z` (quota GLB, meno affidabile ma sempre disponibile).*/
  screwEdgeThicknessMm: number | null;
}

/**
 * Risolve tutte le istanze fisiche del lockstop selezionato, per ciascun lato
 * (joist / header) con il blocco dei suoi connettori affiancati.
 * @returns Array di `LockstopInstance` (puo' essere vuoto se non ci sono connettori o fori standard).*/
export function computeLockstopInstances(input: LockstopPlacementInput): LockstopInstance[] {
  const { features, installationLevels, kind, connectors, glbUrl, screwEdgeThicknessMm } = input;
  const instances: LockstopInstance[] = [];

  const plateRowsYMm = lockstopRowsYMm(features); // ascending: [topY, bottomY, ...]
  const plateTopRowYMm = plateRowsYMm[0];
  if (plateTopRowYMm == null) return instances;
  const plateTopRowColumns = lockstopColumnsForRow(features, plateTopRowYMm);
  if (plateTopRowColumns.length === 0) return instances;
  const plateRef = toConnectorFrame(plateTopRowColumns[0]!, plateTopRowYMm, features);
  // Coordinate centrate dei fori della piastra, per la misura mesh dello
  // spessore locale nel render layer (vedi doc-comment su `LockstopInstance.holesXY`).
  const centeredHoles = features.holes.map((h) => toConnectorFrame(h.x_mm, h.y_mm, features));
  const holesXY: readonly [number, number][] = centeredHoles.map((c) => [c.x, c.y]);
  const rotatedHolesXY: readonly [number, number][] = centeredHoles.map((c) => [-c.x, -c.y]);
  // Foro di riferimento del pezzo RUOTATO di 180°: la sua riga alta e' la riga
  // bassa originale e il suo foro piu' a sinistra e' l'originale piu' a destra.
  // Il centro dei fori della piastra NON coincide per forza col centro del
  // box (LOCKSTOP5: +1.25mm, verificato sul GLB), quindi la rotazione non
  // lascia invariato l'insieme dei fori e serve un'ancora propria.
  const plateBottomRowYMm = plateRowsYMm[plateRowsYMm.length - 1]!;
  const plateBottomRowColumns = lockstopColumnsForRow(features, plateBottomRowYMm);
  const plateRefRotated = toConnectorFrame(
    plateBottomRowColumns[plateBottomRowColumns.length - 1]!,
    plateBottomRowYMm,
    features,
  );

  // installationLevels = quanti PEZZI separati (uno intero per lato), MAI un
  // pezzo a ponte fra i due lati — vedi doc-comment in cima. Per ciascun lato
  // i connettori affiancati formano UN blocco (vedi `selectBlockRows`).
  const sideConnectors = (side: ConnectorSide) =>
    connectors.filter((c) => c.side === side).sort((a, b) => a.index - b.index);
  const sides: ConnectorSide[] = installationLevels === 2 ? ["joist", "header"] : ["joist"];

  for (const side of sides) {
    const members = sideConnectors(side);
    const owner = members[0];
    if (!owner) continue;

    const block = selectBlockRows(owner, members, plateRowsYMm.length);
    const topConnectorRow = block[0];
    if (!topConnectorRow) continue;

    const anchorZ = screwEdgeThicknessMm ?? topConnectorRow.z;
    for (const { xs, mirrored } of targetColumnGroups(topConnectorRow, kind)) {
      // Ruotato 180° attorno al centro: (x,y) -> (-x,-y), foro di riferimento incluso.
      const sign: 1 | -1 = mirrored ? -1 : 1;
      const ref = mirrored ? plateRefRotated : plateRef;
      const localX = xs[0]! - sign * ref.x;
      const localY = topConnectorRow.y - sign * ref.y;
      const worldAnchor = new THREE.Vector3(localX, localY, anchorZ);
      owner.frame.localToWorld(worldAnchor);

      const coveredHoles = findCoveredHoles(owner, members, features, { x: localX, y: localY, z: anchorZ }, sign);

      instances.push({
        id: `${owner.index}:${side}:${xs[0]}`,
        side,
        connectorIndex: owner.index,
        position: [worldAnchor.x, worldAnchor.y, worldAnchor.z],
        quaternion: mirrored ? owner.quaternion.clone().multiply(ROTATE_Z_180) : owner.quaternion.clone(),
        widthMm: features.width_mm,
        heightMm: features.height_mm,
        thicknessMm: features.thickness_mm,
        glbUrl,
        localAnchorZMm: anchorZ,
        coveredHoles,
        holesXY: mirrored ? rotatedHolesXY : holesXY,
      });
    }
  }

  return instances;
}
